import { useOneAdvisor } from 'src/suitability-report/hooks/useOneAdvisor';
import { StepId, NewSuitabilityData } from 'src/suitability-report/types';
import { Box, Flex, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useClientObjectives } from 'src/suitability-report/hooks/useClientObjectives';
import { AdviceHeadlineCarousel } from '../edit/components/AdviceHeadlineCarousel';
import { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useGetAdvice } from './useGetAdvice';
import { useGetTemplateSections } from './useGetTemplateSections';
import { clampIndex } from '../edit/utils/clampIndex';
import { AsyncContent } from '../edit/components/AsyncContent';
import { LoadingAnimation } from 'src/UI/app-container/chat/components/LoadingAnimation';
import { useSectionExpanded, useReportTruncation } from '../edit/components/SiestaSummaryCard';

type AdviceSummaryItem = { id: string; summary?: string | null };

function getAdvisorSubline(
  advisorLoading: boolean,
  advisor: { firstName: string } | null,
): string | undefined {
  if (advisorLoading) return 'Advisor: Loading…';
  if (advisor) return `Advisor: ${advisor.firstName}`;
  return undefined;
}

const LoadingText = () => (
  <Flex direction="column" ta="center" pt="xs">
    <LoadingAnimation />
    <Text pt="sm" c="gray.0" size="xs" fw={FONT_WEIGHTS.medium}>
      Extracting data...
    </Text>
  </Flex>
);

const EmptyText = ({ children }: { children: string }) => (
  <Text c="gray.0" size="sm" fw={FONT_WEIGHTS.regular}>
    {children}
  </Text>
);

function SummaryContent({ items }: { items: AdviceSummaryItem[] }) {
  const isExpanded = useSectionExpanded();
  const reportTruncation = useReportTruncation();
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || isExpanded) return;

    const isTruncated = el.scrollHeight > el.clientHeight;
    reportTruncation(isTruncated);
  }, [items, isExpanded, reportTruncation]);

  return (
    <Box
      ref={boxRef}
      style={
        isExpanded
          ? undefined
          : {
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
      }
    >
      {items.map(x => (
        <Text key={x.id} c="gray.0" size="sm" fw={FONT_WEIGHTS.regular}>
          {x.summary ?? 'No summary'}
        </Text>
      ))}
    </Box>
  );
}

const renderQuickSummary = (items: AdviceSummaryItem[], loading: boolean) => (
  <Box>
    <AsyncContent
      loading={loading}
      hasData={items.length > 0}
      loadingFallback={<EmptyText>Loading…</EmptyText>}
      emptyFallback={<EmptyText>No summary</EmptyText>}
    >
      <SummaryContent items={items} />
    </AsyncContent>
  </Box>
);

function useAutoClampedIndex(len: number) {
  const [index, setIndex] = useState(0);
  const prevLen = useRef(len);

  useEffect(() => {
    if (len > prevLen.current) {
      setIndex(len - 1);
    } else {
      setIndex(i => clampIndex(i, len));
    }
    prevLen.current = len;
  }, [len]);

  return { index, setIndex };
}

type FileItem = { id: string; name: string; dataUploadFileId?: string };

function useActiveAdvice(files: FileItem[]) {
  const { index, setIndex } = useAutoClampedIndex(files.length);

  const activeFile = useMemo(() => files[clampIndex(index, files.length)], [files, index]);
  const activeId = activeFile?.id;
  const adviceQueryId = activeFile?.dataUploadFileId ?? activeFile?.id;

  const { advice, isLoading } = useGetAdvice(adviceQueryId);

  return {
    index,
    setIndex,
    advice,
    isLoading,
    activeId,
  };
}

export function useSiestaSummary(
  stepId: StepId,
  data: NewSuitabilityData,
  setData?: React.Dispatch<React.SetStateAction<NewSuitabilityData>>,
) {
  const advisorId = data.isFamily ? data.family?.advisorUserId : data.client?.advisorUserId;
  const { advisor, isLoading: advisorLoading } = useOneAdvisor(advisorId);

  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;
  const { objectives, isLoading: objectivesLoading } = useClientObjectives(clientId);

  const adviceState = useActiveAdvice(data.adviceFiles);
  const feeState = useActiveAdvice(data.feeDocuments);
  const supportState = useActiveAdvice(data.supportDocuments);

  const { sections: templateSections, isLoading: templateLoading } = useGetTemplateSections(
    data.template?.id,
  );

  const buildClient = () => {
    if (data.isFamily || !data.client) return null;

    return {
      title: 'Client',
      headline: (
        <Text size="xl" fw={FONT_WEIGHTS.bold} c="white">
          {data.client.firstName} {data.client.lastName}
        </Text>
      ),
      subline: getAdvisorSubline(advisorLoading, advisor),
      sections: [
        {
          title: 'Objectives',
          content: (
            <Box>
              <AsyncContent
                loading={objectivesLoading}
                hasData={objectives.length > 0}
                loadingFallback={<LoadingText />}
                emptyFallback={<EmptyText>No objectives added</EmptyText>}
              >
                {objectives.map(o => (
                  <Text key={o.id} c="gray.0" size="sm" fw={FONT_WEIGHTS.regular}>
                    {o.description}
                  </Text>
                ))}
              </AsyncContent>
            </Box>
          ),
        },
      ],
    };
  };

  const buildFamily = () => {
    if (!data.isFamily || !data.family) return null;

    return {
      title: 'Family',
      headline: (
        <Text size="xl" fw={FONT_WEIGHTS.bold} c="white">
          {data.family?.name} family
        </Text>
      ),
      subline: getAdvisorSubline(advisorLoading, advisor),
      sections: [
        {
          title: 'Objectives',
          content: (
            <Box>
              <AsyncContent
                loading={objectivesLoading}
                hasData={objectives.length > 0}
                loadingFallback={<LoadingText />}
                emptyFallback={<EmptyText>No objectives added</EmptyText>}
              >
                {objectives.map(o => (
                  <Text key={o.id} c="gray.0" size="sm" fw={FONT_WEIGHTS.regular}>
                    {o.description}
                  </Text>
                ))}
              </AsyncContent>
            </Box>
          ),
        },
      ],
    };
  };

  const buildFileStep = (files: FileItem[], state: ReturnType<typeof useActiveAdvice>) => {
    if (files.length === 0) return null;

    const grouped = groupAdviceByClass(state.advice);

    let sections;

    if (state.isLoading) {
      sections = [
        {
          title: 'Loading…',
          content: renderQuickSummary([], true),
        },
      ];
    } else if (grouped.length > 0) {
      sections = grouped.map(g => ({
        title: humanizeAdviceClass(g.adviceClass),
        content: renderQuickSummary(
          g.items.map(x => ({ id: x.id, summary: x.summary })),
          false,
        ),
      }));
    } else {
      sections = [
        {
          title: 'Quick summary',
          content: renderQuickSummary([], false),
        },
      ];
    }

    return {
      title: undefined,
      headline: (
        <AdviceHeadlineCarousel
          files={files}
          initialIndex={state.index}
          onIndexChange={state.setIndex}
        />
      ),
      subline: undefined,
      sections,
    };
  };

  const buildTemplate = () => {
    if (!data.template) return null;

    const date = data.template.createdAt;

    const sectionsWithHeader = templateSections.filter(s => !!s.section_header?.trim());

    return {
      title: 'Template',
      headline: (
        <Text size="xl" fw={FONT_WEIGHTS.bold} c="white">
          {data.template.displayName}
        </Text>
      ),
      subline: date
        ? new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }).format(new Date(date))
        : undefined,
      sections: [
        {
          title: 'Report structure',
          content: (
            <Box>
              <AsyncContent
                loading={templateLoading}
                hasData={sectionsWithHeader.length > 0}
                loadingFallback={<LoadingText />}
                emptyFallback={<EmptyText>No report structure found</EmptyText>}
              >
                {sectionsWithHeader.map((o, index) => (
                  <Text key={o.id} c="gray.0" size="sm" fw={FONT_WEIGHTS.regular}>
                    {index + 1}. {o.section_header}
                  </Text>
                ))}
              </AsyncContent>
            </Box>
          ),
        },
      ],
    };
  };

  const activeElementId = useMemo(() => {
    switch (stepId) {
      case 'client':
        return clientId;

      case 'advice':
        return adviceState.activeId;

      case 'documents':
        return feeState.activeId;

      case 'support':
        return supportState.activeId;

      case 'template':
        return data.template?.id;

      default:
        return undefined;
    }
  }, [
    stepId,
    clientId,
    data.template?.id,
    adviceState.activeId,
    feeState.activeId,
    supportState.activeId,
  ]);
  useEffect(() => {
    if (!setData) return;

    setData(prev => {
      if (prev.highlightedElement === activeElementId) return prev;
      return { ...prev, highlightedElement: activeElementId };
    });
  }, [activeElementId, setData]);

  switch (stepId) {
    case 'client':
      return data.isFamily ? buildFamily() : buildClient();

    case 'advice':
      return buildFileStep(data.adviceFiles, adviceState);

    case 'documents':
      return buildFileStep(data.feeDocuments, feeState);

    case 'support':
      return buildFileStep(data.supportDocuments, supportState);

    case 'template':
      return buildTemplate();

    default:
      return null;
  }
}

function groupAdviceByClass<T extends { adviceClass?: string | null }>(items: T[]) {
  const map = new Map<string, T[]>();

  for (const item of items) {
    const key = (item.adviceClass ?? '').trim() || 'Quick summary';
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }

  return Array.from(map.entries()).map(([adviceClass, items]) => ({ adviceClass, items }));
}

const ACRONYMS = new Set(['ISA', 'LTA', 'GIA', 'VCT', 'EIS']);

const LOWERCASE_WORDS = new Set(['of', 'and', 'to', 'for', 'in', 'on']);

function humanizeAdviceClass(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((word, index) => {
      const upper = word.toUpperCase();

      if (ACRONYMS.has(upper)) {
        return upper;
      }

      if (index > 0 && LOWERCASE_WORDS.has(word)) {
        return word;
      }

      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}
